/**
 * Wilf's book — the offline trade round (docs/TRADE-OFFLINE.md; GAMEPLAY-ANALYSIS WP-X2, research R9 / R11 / R14).
 * What is traded, where, and the numbers. Pure data: the goods are the game's own fictional contraband
 * (shared/items.ts — fur coats, wristwatches, perfume, silk, tape decks, tax discs, spirits, cigarettes; no
 * drugs), the buyers are the online market's eight (shared/economy.ts BUYERS) and the prices come from the
 * shared maths (`quoteSale`, `quotePurchase`): district taste, demand that dips as you sell, police attention.
 *
 * Offline-only numbers live here: the four doors you buy at (each with a small lot that comes back every
 * few minutes of play), what Lenny's tips are worth, and the hourly limit that keeps trade below the story's
 * pay (Appendix A: side work ≈ £1.5–2.5k an hour, the story ≈ £5–8k).
 */
import { BUYERS, CARRY_CAPACITY, HOLDALL_BONUS, SHOP_ITEMS, type BuyerType, type EconDistrictId } from '../../shared/economy';
import { ITEM_DEFS } from '../../shared/items';

export const TRADE_GOODS = ['velvet', 'blue-tins', 'ember-gin', 'moth-silk', 'tape-decks', 'tax-discs', 'cut-spirits', 'hooky-cigs'] as const;
export type TradeGood = (typeof TRADE_GOODS)[number];
export type Goods = Partial<Record<TradeGood, number>>;

export const isTradeGood = (id: string): id is TradeGood => (TRADE_GOODS as readonly string[]).includes(id);

/** What the card and the book call a good: plain names, the unit it comes in. */
export const GOOD_LABEL: Record<TradeGood, { name: string; unit: string; units: string }> = {
  velvet: { name: 'Fur coats', unit: 'crate', units: 'crates' },
  'blue-tins': { name: 'Wristwatches', unit: 'watch', units: 'watches' },
  'ember-gin': { name: 'Perfume', unit: 'case', units: 'cases' },
  'moth-silk': { name: 'Silk', unit: 'bolt', units: 'bolts' },
  'tape-decks': { name: 'Tape decks', unit: 'box', units: 'boxes' },
  'tax-discs': { name: 'Tax discs', unit: 'bundle', units: 'bundles' },
  'cut-spirits': { name: 'Spirits', unit: 'case', units: 'cases' },
  'hooky-cigs': { name: 'Cigarettes', unit: 'carton', units: 'cartons' },
};

/** "3 crates of fur coats" / "1 watch". */
export function goodsText(good: TradeGood, qty: number): string {
  const l = GOOD_LABEL[good];
  if (good === 'blue-tins') return `${qty} ${qty === 1 ? 'watch' : 'watches'}`;
  return `${qty} ${qty === 1 ? l.unit : l.units} of ${l.name.toLowerCase()}`;
}

export const goodSize = (g: TradeGood): number => ITEM_DEFS[g].size;
export const goodMaxOnFoot = (g: TradeGood): number => ITEM_DEFS[g].maxOnFoot;

/** One good at a door: Standard price per unit and how many are on the table per restock. */
export interface DoorStock {
  cost: number;
  lot: number;
}

export interface TradePlace {
  id: string;
  /** POI in the authored layout (src/world/lowBorough/data.ts). */
  poi: string;
  /** The place ("Bonded Warehouse No. 3"). */
  name: string;
  /** Who you deal with there. */
  who: string;
  district: EconDistrictId;
  /** Buy here (a door): goods, price, lot. */
  door?: Partial<Record<TradeGood, DoorStock>>;
  /** Sell here: street buyer (3 at a time, barely noticed) or fence (bulk, better price, more attention). */
  buyer?: { type: BuyerType; accepts: readonly TradeGood[] };
  /** Wilf: also sells the canvas holdall. */
  wilf?: boolean;
}

/** Districts of the buyers' POIs (asserted against the layout's `districtAt` in trade.test.ts). */
const BUYER_DISTRICT: Record<string, EconDistrictId> = {
  'phone-parade': 'the-parade',
  'phone-tanners': 'tanners-row',
  'phone-mercy': 'mercy-square',
  'phone-dock': 'gullet-basin',
  'docks-crane-quay': 'gullet-basin',
  'gasworks-yard': 'the-parade',
  'st-brannocs': 'mercy-square',
  'scrapyard-gorringe': 'cinder-yards',
};

const buyerOf = (id: string): TradePlace['buyer'] => {
  const b = BUYERS.find((x) => x.id === id)!;
  return { type: b.type, accepts: b.accepts.filter(isTradeGood) };
};

/**
 * The doors (buy) and the buyers (sell). The doors are the online suppliers' (Bonded No. 3, the Lamp's back
 * door, Gorringe's shed) plus Wilf's arch for the made goods; costs are the online ones where they exist, about
 * half the reference value otherwise. Lots are small on purpose: the tip-offs are what make a round pay.
 */
export const TRADE_PLACES: readonly TradePlace[] = [
  {
    id: 'wilf',
    poi: 'lockup-arch-14',
    name: 'Arch 14, Cinder Yards',
    who: 'Wilf Tuckett',
    district: 'cinder-yards',
    door: { 'tape-decks': { cost: 46, lot: 4 }, 'tax-discs': { cost: 34, lot: 6 } },
    wilf: true,
  },
  {
    id: 'bonded',
    poi: 'docks-bonded-3',
    name: 'Bonded Warehouse No. 3',
    who: 'The night man',
    district: 'gullet-basin',
    door: { velvet: { cost: 130, lot: 2 }, 'ember-gin': { cost: 72, lot: 3 }, 'cut-spirits': { cost: 78, lot: 2 } },
  },
  {
    id: 'lamp',
    poi: 'club-velvet-lamp',
    name: 'The Velvet Lamp, back door',
    who: 'The Vanes’ barman',
    district: 'the-parade',
    door: { 'blue-tins': { cost: 22, lot: 12 }, 'hooky-cigs': { cost: 26, lot: 6 } },
  },
  {
    id: 'gorringe',
    poi: 'scrapyard-gorringe',
    name: 'Gorringe’s yard',
    who: 'Gorringe',
    district: 'cinder-yards',
    door: { 'moth-silk': { cost: 50, lot: 4 }, 'ember-gin': { cost: 76, lot: 3 } },
    buyer: buyerOf('fence-gorringe'),
  },
  ...BUYERS.filter((b) => b.id !== 'fence-gorringe').map(
    (b): TradePlace => ({
      id: b.id,
      poi: b.poi,
      name: b.name,
      who: b.type === 'fence' ? 'A fence' : 'A street buyer',
      district: BUYER_DISTRICT[b.poi] ?? 'the-parade',
      buyer: buyerOf(b.id),
    }),
  ),
];

export const tradePlace = (id: string): TradePlace | undefined => TRADE_PLACES.find((p) => p.id === id);

/** On foot: six units (a holdall adds six), and no more of a bulky good than one person carries (shared/items.ts). */
export const POCKETS = CARRY_CAPACITY;
export const HOLDALL_EXTRA = HOLDALL_BONUS;
/** Wilf's holdall costs what the online shop charges. Options, never power: it is room for goods, nothing else. */
export const HOLDALL_PRICE = SHOP_ITEMS.find((s) => s.defId === 'holdall')?.price ?? 350;
/**
 * Unlock id of the holdall in the save. A contact ladder (WP-X1, Wilf's rung) can grant it with
 * `save.addUnlock(TRADE_HOLDALL_UNLOCK)` instead of the player buying it.
 */
export const TRADE_HOLDALL_UNLOCK = 'trade-holdall';
/** The stash in Lockup 7 (the key you buy, src/economy): safe from the law. Smaller than online's 100. */
export const STASH_CAPACITY = 40;

export const TRADE = {
  /** A door's lot comes back this long (seconds of play) after the first sale from it. */
  restockS: 8 * 60,
  /** Profit the borough stands per rolling hour of play; above it buyers pay only `overCapShare` of the margin. */
  hourCap: 2_500,
  windowS: 3_600,
  overCapShare: 0.25,
  /** Lenny: the first tip this long after the trade opens, then every `tipGapS` (random in the range). */
  firstTipS: 90,
  tipGapS: [360, 540] as const,
  /** At most this many tips open at once. */
  maxTips: 2,
  /** A buyer tip: +20–40 % on one good at one buyer, for 10 minutes of play. */
  buyerTipMul: [1.2, 1.4] as const,
  buyerTipS: 600,
  /** A lot tip: an extra lot at a door at 65 % of the price, for 8 minutes. */
  lotTipMul: 0.65,
  lotTipS: 480,
  /** The Vanes' runner: out for 5 minutes, at most once every 20, about one tip in three when allowed. */
  runnerS: 300,
  runnerGapS: 1_200,
  runnerChance: 0.34,
  /** Deal heat (shared `quoteSale().heat`) below this goes unnoticed. */
  riskFloor: 4,
  /** Chance a deal is clocked: (heat − 2) / riskSpan, at most riskMax; level 2 from `level2Heat`. */
  riskSpan: 28,
  riskMax: 0.75,
  level2Heat: 22,
  /** Seconds a stop on the runner's round lasts (the time to pull the driver out). */
  runnerDwellS: 9,
  /** The runner's load is empty this often ("they'd dropped it already"). */
  runnerEmpty: 0.25,
} as const;

/** The Vanes' runner's round: their club, their pub on Kiln Street, their warehouse on the docks. */
export const RUNNER_STOPS = ['club-velvet-lamp', 'pub-brickmakers', 'docks-bonded-3'] as const;
export const RUNNER_SPEC = 'van';
/** Claret, the Vanes' colour on their motors. */
export const RUNNER_PAINT = 0x5a1c1e;
